import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Tag, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

type Category = {
  id: string;
  name: string;
  created_at: string;
  image_url: string | null;
  slug: string | null;
  display_order: number;
  parent_id: string | null;
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const AdminCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [parentId, setParentId] = useState<string>("none");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("categories")
      .select("*")
      .order("display_order", { ascending: true })
      .order("name", { ascending: true });
    setCategories((data ?? []) as Category[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setSlug("");
    setImageUrl("");
    setDisplayOrder((categories.at(-1)?.display_order ?? 0) + 1);
    setParentId("none");
    setOpen(true);
  };

  const openEdit = (c: Category) => {
    setEditing(c);
    setName(c.name);
    setSlug(c.slug ?? "");
    setImageUrl(c.image_url ?? "");
    setDisplayOrder(c.display_order ?? 0);
    setParentId(c.parent_id ?? "none");
    setOpen(true);
  };

  const handleUpload = async (file: File) => {
    try {
      setUploading(true);
      const ext = file.name.split(".").pop();
      const path = `categories/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from("site-assets")
        .upload(path, file, { cacheControl: "3600", upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from("site-assets").getPublicUrl(path);
      setImageUrl(data.publicUrl);
      toast.success("Image uploaded");
    } catch (e: any) {
      toast.error(e.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error("Name is required");
    if (trimmed.length > 60) return toast.error("Name must be 60 characters or less");

    const dup = categories.find(
      (c) => c.name.toLowerCase() === trimmed.toLowerCase() && c.id !== editing?.id,
    );
    if (dup) return toast.error("That category already exists");

    setSaving(true);
    const payload = {
      name: trimmed,
      slug: (slug.trim() || slugify(trimmed)) || null,
      image_url: imageUrl || null,
      display_order: Number(displayOrder) || 0,
      parent_id: parentId === "none" ? null : parentId,
    };
    const { error } = editing
      ? await supabase.from("categories").update(payload).eq("id", editing.id)
      : await supabase.from("categories").insert(payload);
    setSaving(false);

    if (error) return toast.error(error.message);
    toast.success(editing ? "Category updated" : "Category created");
    setOpen(false);
    load();
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from("categories").delete().eq("id", deleteId);
    if (error) return toast.error(error.message);
    toast.success("Category deleted");
    setDeleteId(null);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Categories</h1>
          <p className="text-sm text-muted-foreground">Organize your product catalog</p>
        </div>
        <Button variant="hero" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Add category
        </Button>
      </div>

      <Card className="border-0 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4">Category</th>
                <th className="text-left p-4 hidden md:table-cell">Slug</th>
                <th className="text-left p-4 hidden sm:table-cell">Order</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={4} className="p-4">
                      <Skeleton className="h-10" />
                    </td>
                  </tr>
                ))
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-muted-foreground">
                    No categories yet. Add your first one.
                  </td>
                </tr>
              ) : (
                (() => {
                  const parents = categories.filter((c) => !c.parent_id);
                  const childrenOf = (id: string) =>
                    categories.filter((c) => c.parent_id === id);
                  const orphans = categories.filter(
                    (c) => c.parent_id && !categories.find((p) => p.id === c.parent_id),
                  );
                  const renderRow = (c: Category, depth: number) => (
                    <tr key={c.id} className="hover:bg-muted/30">
                      <td className="p-4">
                        <div
                          className="flex items-center gap-3"
                          style={{ paddingLeft: depth * 24 }}
                        >
                          {depth > 0 && (
                            <span className="text-muted-foreground/60 select-none">↳</span>
                          )}
                          <div className="h-10 w-10 rounded-lg overflow-hidden bg-primary/10 text-primary shrink-0 grid place-items-center">
                            {c.image_url ? (
                              <img src={c.image_url} alt={c.name} className="h-full w-full object-cover" />
                            ) : (
                              <Tag className="h-4 w-4" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{c.name}</p>
                            {depth === 0 ? (
                              <p className="text-[10px] uppercase tracking-wider text-primary/70 font-semibold">
                                Parent
                              </p>
                            ) : (
                              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                Subcategory
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground hidden md:table-cell">
                        {c.slug || <span className="opacity-50">—</span>}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground hidden sm:table-cell">
                        {c.display_order ?? 0}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(c)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setDeleteId(c.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                  const rows: JSX.Element[] = [];
                  parents.forEach((p) => {
                    rows.push(renderRow(p, 0));
                    childrenOf(p.id).forEach((sub) => rows.push(renderRow(sub, 1)));
                  });
                  orphans.forEach((o) => rows.push(renderRow(o, 1)));
                  return rows;
                })()
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Image</Label>
              <div className="aspect-square w-32 rounded-xl border border-dashed bg-muted/30 relative overflow-hidden flex items-center justify-center">
                {imageUrl ? (
                  <>
                    <img src={imageUrl} alt="" className="h-full w-full object-cover" />
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="absolute top-1 right-1 h-6 px-2 text-xs"
                      onClick={() => setImageUrl("")}
                    >
                      Remove
                    </Button>
                  </>
                ) : uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : (
                  <label className="cursor-pointer text-xs text-muted-foreground flex flex-col items-center gap-1 px-3 text-center">
                    <ImageIcon className="h-5 w-5" />
                    <span>Upload</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cat-name">Name</Label>
              <Input
                id="cat-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!editing && !slug) setSlug(slugify(e.target.value));
                }}
                placeholder="e.g. Sarees"
                maxLength={60}
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cat-slug">Slug / Link</Label>
                <Input
                  id="cat-slug"
                  value={slug}
                  onChange={(e) => setSlug(slugify(e.target.value))}
                  placeholder="sarees"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cat-order">Display order</Label>
                <Input
                  id="cat-order"
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cat-parent">Parent category</Label>
              <Select value={parentId} onValueChange={setParentId}>
                <SelectTrigger id="cat-parent">
                  <SelectValue placeholder="None (top-level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (top-level)</SelectItem>
                  {categories
                    .filter((c) => c.id !== editing?.id && !c.parent_id)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Leave empty to make this a top-level category. Select a parent to make it a subcategory.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="hero" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category?</AlertDialogTitle>
            <AlertDialogDescription>
              Products using this category will be unlinked but not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCategories;
